<template>
  <div class="category-slider">
    <!-- Row: Title | Justification Button | Slider -->
    <div class="category-slider-row">
      <!-- Title with Tooltip -->
      <span
        v-tooltip="{ text: titleTooltip, portal: true, theme: titleTooltipTheme }"
        class="category-slider-label data-v-tooltip">
        {{ label }}
      </span>

      <!-- Justification Button with Tooltip -->
      <button
        v-tooltip="{ text: justificationTooltip, portal: true, theme: tooltipThemeCentered }"
        @click="emit('openJustification')"
        class="flex items-center justify-center flex-shrink-0 w-6 h-6 transition-colors rounded-full data-v-tooltip"
        :class="
          hasJustification
            ? 'bg-neutral-200 text-neutral-500 hover:bg-neutral-300'
            : 'bg-neutral-200 text-neutral-500 hover:bg-neutral-300'
        ">
        <IconPencil class="h-4 w-4" stroke="1.8" />
      </button>

      <!-- Slider - isolate creates stacking context for children without z-index side effects -->
      <div
        ref="trackEl"
        class="relative h-10 min-w-0 overflow-visible slider-track isolate"
        :class="{ 'cursor-not-allowed': isLocked, 'cursor-pointer': !isLocked }"
        @click="handleTrackClick">
        <!-- Track lines (these get opacity when locked) -->
        <div :class="{ 'opacity-60': isLocked }">
          <!-- Track background line -->
          <div class="absolute left-0 right-0 h-1 -translate-y-1/2 bg-neutral-200 rounded top-1/2"></div>

          <!-- Progress fill line -->
          <div
            class="absolute h-1 -translate-y-1/2 rounded-l top-1/2 bg-primary-default"
            :style="{ width: progressWidth }"></div>
        </div>

        <!-- Step markers - outside opacity container for proper tooltip display -->
        <div class="absolute inset-x-0 overflow-visible top-1/2">
          <div
            v-for="(step, index) in sliderSteps"
            :key="step"
            v-tooltip="{ text: getStepTooltip(step), portal: true, theme: tooltipTheme }"
            class="absolute data-v-tooltip z-10 hover:z-[100] flex items-center justify-center"
            :style="{
              left: `${(index / (sliderSteps.length - 1)) * 100}%`,
              width: '24px',
              height: '24px',
              marginLeft: '-12px',
              marginTop: '-12px',
            }">
            <!-- Step dot - hidden at current value when thumb/lock is showing -->
            <div
              class="w-3 h-3 transition-colors rounded-full"
              :class="[
                modelValue === step ? 'opacity-0' : '',
                isLocked ? 'bg-neutral-200' : 'bg-neutral-300',
                !isLocked && modelValue !== step && 'hover:bg-neutral-400',
              ]"></div>
            <!-- Step value label below (only for min, default, max) -->
            <span
              v-if="getStepLabel(step)"
              class="absolute top-full mt-0.5 text-xs font-semibold text-neutral-600 whitespace-nowrap"
              :class="{ 'opacity-60': isLocked }">
              {{ getStepLabel(step) }}
            </span>
          </div>
        </div>

        <!-- Thumb (when unlocked) - shows step tooltip for current value -->
        <div
          v-if="!isLocked"
          v-tooltip="{ text: getStepTooltip(modelValue), portal: true, theme: tooltipTheme }"
          class="absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-white border-2 border-primary-default rounded-full shadow cursor-grab active:cursor-grabbing z-[200] data-v-tooltip"
          :style="{ left: progressWidth, transform: 'translate(-50%, -50%)' }"
          @mousedown="startDrag"></div>

        <!-- Lock icon (when locked) - larger hit area for tooltip -->
        <div
          v-if="isLocked"
          v-tooltip="{ text: wt('complexity', 'slider', 'locked'), portal: true, theme: tooltipThemeCentered }"
          class="absolute top-1/2 flex items-center justify-center cursor-pointer z-[200] data-v-tooltip"
          :style="{ left: progressWidth, width: '32px', height: '32px', marginLeft: '-16px', marginTop: '-16px' }"
          @click.stop="emit('openJustification')">
          <div class="flex items-center justify-center w-6 h-6 bg-neutral-200 rounded-full hover:bg-neutral-300">
            <IconLock class="h-4 w-4 text-neutral-500" stroke="2" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRef } from 'vue'
import { IconLock, IconPencil } from '@tabler/icons-vue'
import { useWidgetText } from '@/helpers/useWidgetText'

const SLIDER_STEPS = [0.8, 0.9, 1.0, 1.1, 1.2, 1.3, 1.4] as const
const DEFAULT_VALUE = 1.0

interface Props {
  modelValue: number
  label: string
  categoryKey: string
  hasJustification: boolean
  isLocked: boolean
  role?: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: number]
  openJustification: []
}>()

const { wt } = useWidgetText('widget-texts-project-setup', toRef(props, 'role'))

const sliderSteps = SLIDER_STEPS
const trackEl = ref<HTMLElement | null>(null)

const justificationTooltip = computed(() =>
  props.hasJustification
    ? wt('complexity', 'slider', 'editJustification')
    : wt('complexity', 'slider', 'addJustification')
)

const titleTooltip = computed(() => wt('complexity', 'dimensions', props.categoryKey, 'tooltip'))

// Shared tooltip theme for consistent styling across all tooltips in the complexity slider
const tooltipTheme = { placement: 'top', width: '14rem', padding: '0.6em 1em' } as const
const tooltipThemeCentered = { placement: 'top', width: '14rem', padding: '0.6em 1em', 'text-align': 'center' } as const
const titleTooltipTheme = { placement: 'top', width: '18rem', padding: '0.6em 1em' } as const

// Get translated tooltip for step value
// Note: Using bracket notation because the key contains a dot (e.g., "0.8")
// which would otherwise be interpreted as a path separator by vue-i18n
function getStepTooltip(step: number): string {
  const key = 'v' + step.toFixed(1).replace('.', '_')
  return wt('complexity', 'slider', 'value', key)
}

// Get label for step (only show for min, default, max)
function getStepLabel(step: number): string {
  if (step === SLIDER_STEPS[0]) return '-'
  if (step === DEFAULT_VALUE) return '1.0'
  if (step === SLIDER_STEPS[SLIDER_STEPS.length - 1]) return '+'
  return ''
}

// Calculate progress width as percentage
const progressWidth = computed(() => {
  const value = props.modelValue ?? DEFAULT_VALUE
  const index = SLIDER_STEPS.indexOf(value as (typeof SLIDER_STEPS)[number])
  if (index === -1) {
    // Find closest value
    const closest = SLIDER_STEPS.reduce((prev, curr) => (Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev))
    const closestIndex = SLIDER_STEPS.indexOf(closest)
    return `${(closestIndex / (SLIDER_STEPS.length - 1)) * 100}%`
  }
  return `${(index / (SLIDER_STEPS.length - 1)) * 100}%`
})

function handleTrackClick(event: MouseEvent) {
  if (props.isLocked) {
    emit('openJustification')
    return
  }

  const track = event.currentTarget as HTMLElement
  const rect = track.getBoundingClientRect()
  const x = event.clientX - rect.left
  const percentage = x / rect.width

  // Find closest step
  const stepIndex = Math.round(percentage * (SLIDER_STEPS.length - 1))
  const clampedIndex = Math.max(0, Math.min(SLIDER_STEPS.length - 1, stepIndex))
  const newValue = SLIDER_STEPS[clampedIndex]

  emit('update:modelValue', newValue)
}

let isDragging = false

function startDrag(event: MouseEvent) {
  if (props.isLocked) return

  isDragging = true
  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)
  event.preventDefault()
}

function onDrag(event: MouseEvent) {
  if (!isDragging) return

  const track = trackEl.value
  if (!track) return

  const rect = track.getBoundingClientRect()
  const x = event.clientX - rect.left
  const percentage = Math.max(0, Math.min(1, x / rect.width))

  // Find closest step
  const stepIndex = Math.round(percentage * (SLIDER_STEPS.length - 1))
  const clampedIndex = Math.max(0, Math.min(SLIDER_STEPS.length - 1, stepIndex))
  const newValue = SLIDER_STEPS[clampedIndex]

  emit('update:modelValue', newValue)
}

function stopDrag() {
  isDragging = false
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
}
</script>

<style scoped>
.category-slider {
  @apply py-2 pl-0 pr-2 overflow-visible;
}

.category-slider-row {
  display: grid;
  grid-template-columns: var(--complexity-label-width, 7rem) 1.5rem minmax(8rem, 1fr);
  align-items: center;
  column-gap: 0.75rem;
}

.category-slider-label {
  @apply min-w-0 cursor-help truncate font-medium text-neutral-800;
}
</style>
